import React, { useEffect, useRef, useState } from 'react'
import { Box } from '@mui/material'
import { CheckCircle, QrCode, Close } from '@mui/icons-material'
import {
  Button,
  CircularProgress,
  Dialog,
  FormControl,
  IconButton,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  Typography
} from '@material-ui/core'
import getTableOptions from 'utils/getTableOptions'
import Wrapper from 'components/Wrapper'
import BasicTable from 'components/BasicTable'
import ResourceNotFound from 'components/ResourceNotFound'
import { useSelector, useDispatch } from 'react-redux'
import attendanceSlice, { fetchAttendeesAttendance } from 'store/attendance'
import { caseInsensitiveSort, getDatesInRange } from 'utils'
import moment from 'moment-timezone'
import { Roles } from 'constants/index'
import { Download } from '@material-ui/icons'
import { downloadAttendanceReportInCSV } from 'utils/downloadCSV'
import QRCode from 'react-qr-code'
import { toPng } from 'html-to-image'

const AttendanceListing = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const qrCodeRef = useRef(null)

  const [showQrCodeDialog, setShowQrCodeDialog] = useState(false)

  const { user } = useSelector((state) => state.auth)
  const {
    conferences,
    inProgress: confProgress,
    conference
  } = useSelector((state) => state.conference)
  const { inProgress, attendeesAttendance, confFilter, currentPage, pageSize } = useSelector(
    (state) => state.attendance
  )

  const activeConferences = conferences?.filter(
    (row) => row.isActive === true && row?.enableAttendance
  )
  const orderedConferences = caseInsensitiveSort(activeConferences, 'confName')

  const selectedConference = orderedConferences.find((conference) => conference?.id === confFilter)

  useEffect(() => {
    if (confFilter && user.role === Roles.SUPER_ADMIN && !confProgress) {
      conferences.every((conf) => {
        if (conf.id === confFilter && conf.isActive === false) {
          dispatch(attendanceSlice.actions.setConferenceFilter(orderedConferences[0]?.id))
          return false
        }
        return true
      })
    }
  }, [confProgress, confFilter])

  useEffect(() => {
    if (user.role === Roles.SUPER_ADMIN && !confFilter && !confProgress) {
      dispatch(attendanceSlice.actions.setConferenceFilter(orderedConferences[0]?.id))
    }
  }, [confProgress, confFilter])

  const getConfDatesBasedOnRole = () => {
    let startDate = ''
    let endDate = ''
    if (user?.role === Roles.SUPER_ADMIN && selectedConference?.id) {
      startDate = moment(selectedConference?.startDate)
        .tz(selectedConference?.timezone)
        ?.format('MM/DD/YYYY')
      endDate = moment(selectedConference?.endDate)
        .tz(selectedConference?.timezone)
        ?.format('MM/DD/YYYY')
    } else if (user?.role === Roles.ADMIN && conference?.id) {
      startDate = moment(conference?.startDate).tz(conference?.timezone)?.format('MM/DD/YYYY')
      endDate = moment(conference?.endDate).tz(conference?.timezone)?.format('MM/DD/YYYY')
    }
    return getDatesInRange(startDate, endDate)
  }

  useEffect(() => {
    if (user?.role === Roles.SUPER_ADMIN && confFilter) {
      dispatch(fetchAttendeesAttendance(confFilter))
    } else if (user?.role === Roles.ADMIN && user?.conferenceId) {
      dispatch(fetchAttendeesAttendance(user?.conferenceId))
    }
  }, [conference?.id, confFilter])

  const handleDownloadCSV = () => {
    const conferenceDates = getConfDatesBasedOnRole()
    const confName =
      user?.role === Roles.SUPER_ADMIN ? selectedConference?.confName : conference?.confName
    downloadAttendanceReportInCSV(user?.role, attendeesAttendance, confName, conferenceDates)
  }

  const handleCloseQrCodeDialog = () => {
    setShowQrCodeDialog(false)
  }

  const handleDownloadQrCode = () => {
    toPng(qrCodeRef.current)
      .then(function (dataUrl) {
        const link = document.createElement('a')
        link.href = dataUrl
        link.download = `${
          user?.role === Roles.SUPER_ADMIN && confFilter
            ? selectedConference.confName
            : conference?.confName
        }_attendance_qr_code.png`
        link.click()
      })
      .catch(function (error) {
        console.error('Error generating QR code:', error)
      })
  }

  const conferenceDates = getConfDatesBasedOnRole()
  const conferenceDatesWithOptions = conferenceDates.map((date) => ({
    name: date,
    options: {
      filter: false,
      sort: false
    }
  }))
  const tableHead = ['Last Name', 'First Name', 'Phone Number', ...conferenceDatesWithOptions]

  const formatTableData = (attendees) =>
    attendees.map((attendee) => {
      const data = [attendee?.lastName, attendee?.firstName, attendee?.phoneNumber]
      attendee.attendance.forEach((attendance) => {
        data.push(
          attendance.attendanceFound ? (
            <CheckCircle sx={{ color: '#388e3c', fontSize: '28px', marginLeft: '1rem' }} />
          ) : (
            ''
          )
        )
      })
      return data
    })

  const options = getTableOptions()

  return (
    <Box>
      <Box className={classes.headBar}>
        <Typography variant="h4">Education Credits</Typography>
        <Box className={classes.actions}>
          {user?.role === Roles.SUPER_ADMIN && orderedConferences.length > 0 && (
            <Box className={classes.filterDropdown}>
              <Typography className={classes.filterLabel}>Conference: </Typography>
              {confProgress ? (
                <CircularProgress size={25} />
              ) : (
                <FormControl fullWidth variant="outlined" size="small" className={classes.select}>
                  <Select
                    value={confFilter}
                    onChange={(event) =>
                      dispatch(attendanceSlice.actions.setConferenceFilter(event.target.value))
                    }>
                    {orderedConferences.map((conf) => (
                      <MenuItem value={conf?.id} key={conf?.id}>
                        {conf?.confName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          )}
          {((user?.role === Roles.SUPER_ADMIN && orderedConferences.length > 0) ||
            user?.role === Roles.ADMIN) && (
            <Box className={classes.actionBtns}>
              {attendeesAttendance.length > 0 && (
                <Button
                  size="medium"
                  className={classes.button}
                  startIcon={<Download />}
                  variant="contained"
                  color="primary"
                  onClick={handleDownloadCSV}>
                  Download Attendance Report
                </Button>
              )}
              <Button
                size="medium"
                className={classes.qrCodeButton}
                startIcon={<QrCode />}
                variant="contained"
                onClick={() => setShowQrCodeDialog(true)}>
                Generate QR Code
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      <Wrapper inProgress={inProgress}>
        {attendeesAttendance?.length ? (
          <Paper variant="outlined" style={{ marginTop: 20 }}>
            <BasicTable
              data={formatTableData(attendeesAttendance)}
              columns={tableHead}
              options={{
                ...options,
                onChangePage: (page) => dispatch(attendanceSlice.actions.setCurrentPage(page)),
                onChangeRowsPerPage: (size) => dispatch(attendanceSlice.actions.setPageSize(size)),
                page: currentPage,
                rowsPerPage: pageSize
              }}
            />
          </Paper>
        ) : (
          !inProgress && (
            <ResourceNotFound
              message={
                user?.role === Roles.SUPER_ADMIN && orderedConferences.length === 0
                  ? 'No conferences found with the attendance module enabled. Please enable the attendance module to view attendance.'
                  : 'No attendance data available for this conference'
              }
            />
          )
        )}
      </Wrapper>
      <Dialog open={showQrCodeDialog} onClose={handleCloseQrCodeDialog} fullWidth maxWidth="md">
        <Box className={classes.dialogTitle}>
          <Typography variant="h5">Attendance QR Code</Typography>
          <IconButton onClick={handleCloseQrCodeDialog} className={classes.iconBtn}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <Box className={classes.dialogContent}>
          <Box className={classes.qrHeader}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Download />}
              className={classes.downloadBtn}
              color="primary"
              onClick={handleDownloadQrCode}>
              Download QR Code
            </Button>
          </Box>
          <Box ref={qrCodeRef} className={classes.qrCodeContainer}>
            <QRCode
              value={
                user?.role === Roles.SUPER_ADMIN && confFilter
                  ? selectedConference.confCode
                  : conference?.confCode
              }
              size={480}
            />
          </Box>
        </Box>
      </Dialog>
    </Box>
  )
}

export default AttendanceListing

const useStyles = makeStyles({
  absent: { marginLeft: '0.5rem', fontWeight: 500 },
  headBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 4px'
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  button: {
    textTransform: 'none',
    fontWeight: 500,
    minWidth: '250px',
    fontSize: '15px'
  },
  select: {
    minWidth: '240px'
  },
  filterDropdown: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end'
  },
  filterLabel: {
    width: '100%',
    fontSize: '1rem',
    textAlign: 'right',
    marginRight: '8px',
    fontWeight: 'bold'
  },
  downloadCSV: { width: '100%', paddingLeft: '1.25rem' },
  selectBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px'
  },
  iconBtn: {
    padding: '4px'
  },
  icon: {
    fontSize: '18px'
  },
  actionBtns: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  flexBox: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center'
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1rem 1rem 1rem 1.5rem'
  },
  qrCodeButton: {
    color: '#fff',
    backgroundColor: '#f57c00',
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '15px',
    '&:hover': {
      backgroundColor: '#ef6c00'
    }
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '1rem 1rem 2rem 1rem'
  },
  qrCodeContainer: {
    backgroundColor: '#fff',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '560px',
    height: '560px',
    padding: '1rem'
  },
  downloadBtn: {
    textTransform: 'none',
    fontWeight: 500,
    letterSpacing: '0.4px',
    fontSize: '15px'
  },
  qrHeader: {
    width: '480px',
    display: 'flex',
    justifyContent: 'end'
  }
})
